import styles from "./styles";
import { Text, View } from "@react-pdf/renderer";

// Roman numeral mapping
const romanNumerals = {
  I: 1,
  II: 2,
  III: 3,
  IV: 4,
  V: 5,
} as const;

// Reverse mapping for converting numbers to Roman numerals
const numberToRoman = {
  1: "I",
  2: "II",
  3: "III",
  4: "IV",
  5: "V",
} as const;

// Helper function to convert number to Roman numeral
const convertToRoman = (num: number): string => {
  if (num >= 1 && num <= 5) {
    return numberToRoman[num as keyof typeof numberToRoman];
  }
  return num.toString(); // fallback to regular number if > 5
};

// const tableData = {
//   headerRow: [
//     "Yıl",
//     "Dış Borç Miktarı (Milyon Altın Lira)",
//     "Toplam Gelirler (Milyon Altın Lira)",
//   ],
//   dataRows: [
//     ["1875", "200", "18"],
//     ["1881", "150", "15"],
//     ["1900", "120", "25"],
//   ],
// };

// Function to process text and convert Roman numeral lists to standard numbered lists
const processMarkdownText = (text: string = "") => {
  if (!text) return "";

  // Check if text contains Roman numeral lists
  const romanPattern = /^(I{1,3}|IV|V)\.\s+(.+)$/gm;
  const hasRomanNumeralsInContent = romanPattern.test(text);

  if (hasRomanNumeralsInContent) {
    // Reset the regex for actual replacement
    romanPattern.lastIndex = 0;
    // Convert Roman numeral lists to standard numbered lists
    let processedText = text.replace(romanPattern, (match, roman, content) => {
      const number = romanNumerals[roman as keyof typeof romanNumerals];
      return number ? `${number}. ${content}` : match;
    });

    return processedText;
  }

  return text;
};

// Improved table detection and parsing
const extractTable = (text: string) => {
  const lines = text.split("\n");
  let tableStart = -1;
  let tableEnd = -1;

  // Find table start and end
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Check if line looks like a table row (contains | and has content)
    if (line.includes("|") && line.length > 2) {
      if (tableStart === -1) {
        tableStart = i;
      }
      tableEnd = i;
    } else if (tableStart !== -1 && line.trim() === "") {
      // Empty line after table - continue looking for more table
      continue;
    } else if (tableStart !== -1 && !line.includes("|")) {
      // Non-table line after table content - table has ended
      break;
    }
  }

  if (tableStart === -1 || tableEnd === -1 || tableEnd - tableStart < 1) {
    return null;
  }

  const tableLines = lines
    .slice(tableStart, tableEnd + 1)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  if (tableLines.length < 2) return null;

  // Parse header (first line)
  const headerRow = tableLines[0]
    .split("|")
    .map((cell) => cell.trim())
    .filter((cell) => cell.length > 0);

  // Find separator line (line with dashes, colons, etc.)
  let separatorIndex = -1;
  for (let i = 1; i < tableLines.length; i++) {
    if (/^[\s\-\:\|]+$/.test(tableLines[i])) {
      separatorIndex = i;
      break;
    }
  }

  // If no separator found, assume second line is separator
  if (separatorIndex === -1) {
    separatorIndex = 1;
  }

  // Parse data rows (after separator)
  const dataRows = tableLines
    .slice(separatorIndex + 1)
    .map((line) =>
      line
        .split("|")
        .map((cell) => cell.trim())
        .filter((cell) => cell.length > 0),
    )
    .filter((row) => row.length > 0);

  if (headerRow.length === 0 || dataRows.length === 0) return null;

  return {
    tableData: { headerRow, dataRows },
    beforeTable: lines.slice(0, tableStart).join("\n").trim(),
    afterTable: lines
      .slice(tableEnd + 1)
      .join("\n")
      .trim(),
  };
};

// Parse content into chunks
interface ContentChunk {
  type: "text" | "table" | "ordered-list" | "unordered-list";
  content: string | { headerRow: string[]; dataRows: string[][] };
}

const parseContentChunks = (text: string): ContentChunk[] => {
  const chunks: ContentChunk[] = [];
  let currentText = text.trim();

  // First, check for tables
  const tableResult = extractTable(currentText);
  if (tableResult) {
    // Add content before table
    if (tableResult.beforeTable) {
      chunks.push({ type: "text", content: tableResult.beforeTable });
    }

    // Add table
    chunks.push({ type: "table", content: tableResult.tableData });

    // Recursively parse content after table
    if (tableResult.afterTable) {
      const afterChunks: ContentChunk[] = parseContentChunks(
        tableResult.afterTable,
      );
      chunks.push(...afterChunks);
    }

    return chunks;
  }

  // Check for ordered lists
  const listMatch = currentText.match(/^(\d+\.\s+.+(?:\n\d+\.\s+.+)*)/m);
  if (listMatch && listMatch.index !== undefined) {
    const beforeList = currentText.substring(0, listMatch.index).trim();
    const listContent = listMatch[1];
    const afterList = currentText
      .substring(listMatch.index + listMatch[0].length)
      .trim();

    if (beforeList) {
      chunks.push({ type: "text", content: beforeList });
    }

    chunks.push({ type: "ordered-list", content: listContent });

    if (afterList) {
      const afterChunks: ContentChunk[] = parseContentChunks(afterList);
      chunks.push(...afterChunks);
    }

    return chunks;
  }

  // Check for unordered lists
  const unorderedListMatch = currentText.match(
    /^([-*+]\s+.+(?:\n[-*+]\s+.+)*)/m,
  );
  if (unorderedListMatch && unorderedListMatch.index !== undefined) {
    const beforeList = currentText
      .substring(0, unorderedListMatch.index)
      .trim();
    const listContent = unorderedListMatch[1];
    const afterList = currentText
      .substring(unorderedListMatch.index + unorderedListMatch[0].length)
      .trim();

    if (beforeList) {
      chunks.push({ type: "text", content: beforeList });
    }

    chunks.push({ type: "unordered-list", content: listContent });

    if (afterList) {
      const afterChunks: ContentChunk[] = parseContentChunks(afterList);
      chunks.push(...afterChunks);
    }

    return chunks;
  }

  // If no special content found, treat as regular text
  if (currentText) {
    chunks.push({ type: "text", content: currentText });
  }

  return chunks;
};

// Parse ordered list
const parseOrderedList = (text: string) => {
  const lines = text.split("\n");
  const listItems = [];

  for (const line of lines) {
    const trimmed = line.trim();
    const match = trimmed.match(/^(\d+)\.\s+(.+)$/);
    if (match) {
      listItems.push({
        number: parseInt(match[1]),
        content: match[2],
      });
    }
  }

  return listItems.length > 0 ? listItems : null;
};

// Parse unordered list
const parseUnorderedList = (text: string) => {
  const lines = text.split("\n");
  const listItems = [];

  for (const line of lines) {
    const trimmed = line.trim();
    const match = trimmed.match(/^[-*+]\s+(.+)$/);
    if (match) {
      listItems.push(match[1]);
    }
  }

  return listItems.length > 0 ? listItems : null;
};

// Render table using @react-pdf/renderer components
export const renderTable = (
  tableData: { headerRow: string[]; dataRows: string[][] },
  key: number,
) => {
  const colWidth = `${100 / tableData.headerRow.length}%`;
  console.log("renderTable with", tableData);

  return (
    <View
      key={key}
      style={{
        width: "100%",
        marginTop: 8,
        marginBottom: 8,
      }}
    >
      {/* Table Header */}
      <View style={{ flexDirection: "row" }}>
        {tableData.headerRow.map((header, index) => (
          <View
            key={index}
            style={{
              width: colWidth,
              borderStyle: "solid",
              borderWidth: 1,
              borderColor: "#000000",
              borderLeftWidth: index === 0 ? 1 : 0,
              paddingTop: 6,
              paddingBottom: 6,
              paddingLeft: 6,
              paddingRight: 6,
              backgroundColor: "#bdbdbd",
            }}
          >
            <Text
              style={{
                fontSize: 8,
                fontWeight: "bold",
                textAlign: "center",
                color: "#000000",
              }}
            >
              {header}
            </Text>
          </View>
        ))}
      </View>

      {/* Table Rows */}
      {tableData.dataRows.map((row, rowIndex) => (
        <View key={rowIndex} style={{ flexDirection: "row" }}>
          {row.map((cell, cellIndex) => (
            <View
              key={cellIndex}
              style={{
                width: colWidth,
                borderStyle: "solid",
                borderWidth: 1,
                borderColor: "#000000",
                borderLeftWidth: cellIndex === 0 ? 1 : 0,
                borderTopWidth: 0,
                paddingTop: 6,
                paddingBottom: 6,
                paddingLeft: 6,
                paddingRight: 6,
              }}
            >
              <Text
                style={{
                  fontSize: 8,
                  textAlign: "center",
                  color: "#000000",
                }}
              >
                {cell}
              </Text>
            </View>
          ))}
        </View>
      ))}
    </View>
  );
};

// Render ordered list using @react-pdf/renderer components
const renderOrderedList = (
  listItems: { number: number; content: string }[],
  key: number,
) => {
  return (
    <View key={key} style={styles.listContainer}>
      {listItems.map((item, index) => (
        <View key={index} style={styles.listItem}>
          <Text style={styles.listNumber}>{convertToRoman(item.number)}.</Text>
          <View style={styles.listContent}>
            <Text style={{ fontWeight: 400 }}>
              {renderInlineMarkdown(item.content, index)}
            </Text>
          </View>
        </View>
      ))}
    </View>
  );
};

// Render unordered list using @react-pdf/renderer components
const renderUnorderedList = (listItems: string[], key: number) => {
  return (
    <View key={key} style={styles.listContainer}>
      {listItems.map((item, index) => (
        <View key={index} style={styles.listItem}>
          <Text style={styles.listBullet}>•</Text>
          <View style={styles.listContent}>
            {renderInlineMarkdown(item, index)}
          </View>
        </View>
      ))}
    </View>
  );
};

// Render inline markdown (bold, italic) within text
const renderInlineMarkdown = (text: string, key: number) => {
  // Split text to capture both bold (**text**) and italic (*text*) patterns
  const parts = text.split(/(\*\*.*?\*\*|\*[^*]+?\*)/g);
  return (
    <Text key={key} style={styles.questionBody}>
      {parts.map((part, index) => {
        if (part.startsWith("**") && part.endsWith("**")) {
          return (
            <Text key={index} style={styles.textBold}>
              {part.slice(2, -2)}
            </Text>
          );
        } else if (part.startsWith("*") && part.endsWith("*")) {
          return (
            <Text key={index} style={styles.textItalic}>
              {part.slice(1, -1)}
            </Text>
          );
        }
        return part;
      })}
    </Text>
  );
};

// Render regular text with paragraph support
const renderTextContent = (text: string, key: number) => {
  const paragraphs = text.split("\n\n").filter((p) => p.trim());

  if (paragraphs.length > 1) {
    return (
      <View key={key}>
        {paragraphs.map((paragraph, index) => (
          <View
            key={index}
            style={[
              styles.paragraphContainer,
              { marginBottom: index < paragraphs.length - 1 ? 8 : 0 },
            ]}
          >
            {renderInlineMarkdown(paragraph, index)}
          </View>
        ))}
      </View>
    );
  }

  return renderInlineMarkdown(text, key);
};

const renderMarkdownText = (text: string = "") => {
  const processedText = processMarkdownText(text);
  const chunks = parseContentChunks(processedText);

  if (chunks.length === 0) {
    return null;
  }

  if (chunks.length === 1 && chunks[0].type === "text") {
    return renderTextContent(chunks[0].content as string, 0);
  }

  return (
    <View>
      {chunks.map((chunk: ContentChunk, index: number) => {
        switch (chunk.type) {
          case "table":
            return renderTable(
              chunk.content as { headerRow: string[]; dataRows: string[][] },
              index,
            );

          case "ordered-list":
            const orderedItems = parseOrderedList(chunk.content as string);
            return orderedItems ? renderOrderedList(orderedItems, index) : null;

          case "unordered-list":
            const unorderedItems = parseUnorderedList(chunk.content as string);
            return unorderedItems
              ? renderUnorderedList(unorderedItems, index)
              : null;

          case "text":
          default:
            return renderTextContent(chunk.content as string, index);
        }
      })}
    </View>
  );
};

export default renderMarkdownText;
